/**
 * 条件字段的枚举可选值
 *
 * 为什么需要这份定义：
 * 枚举字段的「值」在数据库里是英文原始值（male / fair / high / en ...），
 * 以前在条件编辑页是纯文本输入框，运营必须手打这些值 —— 打错不报错，
 * 只表现为「条件配了却永远没人命中」，极难排查。
 *
 * 现在编辑页选中枚举字段后，值改为下拉选择；条件列表页也用它把原始值
 * 翻译成本地化标签。两个页面共用这一份定义，避免再出现「两处硬编码不同步」。
 *
 * 新增枚举字段时：在这里加一项 + 在 i18n 的 zh.js / en.js 加对应 condVal* 文案
 * + 后端 AutoPushService::getUserFieldValue() 支持该字段。
 */

/** 字段 => [{ value: 数据库原始值, labelKey: i18n key（不含 autoPush. 前缀） }] */
export const ENUM_FIELD_VALUE_OPTIONS = {
  gender: [
    { value: 'male', labelKey: 'condValGenderMale' },
    { value: 'female', labelKey: 'condValGenderFemale' },
    { value: 'other', labelKey: 'condValGenderOther' }
  ],
  language: [
    { value: 'zh', labelKey: 'condValLanguageZh' },
    { value: 'en', labelKey: 'condValLanguageEn' }
  ],
  selfCareLevel: [
    { value: 'excellent', labelKey: 'condValSelfCareExcellent' },
    { value: 'good', labelKey: 'condValSelfCareGood' },
    { value: 'fair', labelKey: 'condValSelfCareFair' },
    { value: 'poor', labelKey: 'condValSelfCarePoor' }
  ],
  diseaseStage: [
    { value: 'early', labelKey: 'condValDiseaseEarly' },
    { value: 'middle', labelKey: 'condValDiseaseMiddle' },
    { value: 'late', labelKey: 'condValDiseaseLate' }
  ],
  burdenLevel: [
    { value: 'low', labelKey: 'condValBurdenLow' },
    { value: 'medium', labelKey: 'condValBurdenMedium' },
    { value: 'high', labelKey: 'condValBurdenHigh' }
  ]
}

/** 该字段是否为枚举字段（决定「值」用下拉还是输入框） */
export function isEnumField(field) {
  return Object.prototype.hasOwnProperty.call(ENUM_FIELD_VALUE_OPTIONS, field)
}

/** 枚举字段的候选值列表；非枚举字段返回空数组 */
export function enumValueOptions(field) {
  return ENUM_FIELD_VALUE_OPTIONS[field] || []
}

/** 枚举值 => i18n label key；非枚举字段或未知值返回 null（调用方回落显示原始值） */
export function enumValueLabelKey(field, value) {
  const hit = enumValueOptions(field).find(function (opt) {
    return opt.value === value
  })
  return hit ? hit.labelKey : null
}

/**
 * 字段 => i18n key（不含 autoPush. 前缀）
 *
 * 顺序即下拉框的展示顺序（运营习惯：从最常用的资料字段到行为类字段）；
 * 编辑页的字段下拉、条件列表与任务页的条件摘要都从这里取，不再各自硬编码。
 */
export const FIELD_LABEL_KEYS = {
  stressLevel: 'condFieldStress',
  selfCareLevel: 'condFieldSelfCare',
  burdenLevel: 'condFieldBurden',
  diseaseStage: 'condFieldDisease',
  age: 'condFieldAge',
  gender: 'condFieldGender',
  currentWeek: 'condFieldWeek',
  daysSinceRegistration: 'condFieldRegDays',
  viewCount: 'condFieldView',
  listenDuration: 'condFieldListen',
  unreadCount: 'condFieldUnread',
  unreadCount7d: 'condFieldUnread7d',
  consecutiveUnreadCount: 'condFieldConsecutiveUnread',
  // 语音日记打卡：三种口径（本周未录 = 0；最近 N 天未录 = 大于等于 N；
  // 含从未录音的版本，把「从没录过」当 999 天，配合「全部满足」也能覆盖他们）
  vjCountThisWeek: 'condFieldVjThisWeek',
  daysSinceLastVj: 'condFieldVjDays',
  vjDaysSinceLastOrNever: 'condFieldVjDaysOrNever',
  language: 'condFieldLanguage'
}

/** 操作符（前端值）=> i18n key；顺序即下拉顺序 */
export const OPERATOR_LABEL_KEYS = {
  eq: 'condOpEq',
  neq: 'condOpNeq',
  gt: 'condOpGt',
  gte: 'condOpGte',
  lt: 'condOpLt',
  lte: 'condOpLte',
  contains: 'condOpContains',
  notContains: 'condOpNotContains'
}

/** 字段的 i18n key；未知字段返回 null（调用方回落显示原始 key） */
export function fieldLabelKey(field) {
  return FIELD_LABEL_KEYS[field] || null
}

/** 操作符的 i18n key；未知操作符返回 null */
export function operatorLabelKey(operator) {
  return OPERATOR_LABEL_KEYS[operator] || null
}
